import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Input, Select, Btn, Table, RNLLoader, BackButton, Modal, Spinner } from '../UI';
import Pagination from '../requisition/Pagination';
import { fmt } from '../../utils';
import * as api from '../../api';
import { REQ_PAYMENT_STATUSES, REQ_PAYMENT_STATUS_COLORS } from './pvUtils';

const NUMERIC_COLS = new Set(['approved_amount', 'processed_amount', 'balance_amount', 'lines_available']);

function SortHeader({ label, colKey, sortKey, sortDir, onSort }) {
  const active = sortKey === colKey;
  return (
    <span
      onClick={() => onSort(colKey)}
      style={{
        cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 4, whiteSpace: 'nowrap', width: '100%', height: '100%',
        margin: '-11px -14px', padding: '11px 14px',
      }}
    >
      {label}
      <span style={{ fontSize: 9, opacity: active ? 1 : 0.35, lineHeight: 1, flexShrink: 0 }}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </span>
  );
}

export default function LoadApprovedRequisitionTable({ onLoad, onBack, toast }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [markingRow, setMarkingRow] = useState(null);    // row to confirm
  const [markBusy, setMarkBusy]     = useState(false);

  const handleSort = (key) => {
    setSortKey(prev => {
      if (prev === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return key; }
      setSortDir('asc');
      return key;
    });
  };

  // Filter values — all sent to the backend
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');

  // Dropdown options built from a full (non-paginated) fetch on mount
  const [projectOptions, setProjectOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);

  // Status filter stays client-side: it's a derived label, not a DB column
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingRowId, setLoadingRowId] = useState(null);

  // Populate dropdown options once on mount from the full dataset
  useEffect(() => {
    api.listApprovedRequisitionsForVoucher({ limit: 500, skip: 0 }).then(res => {
      const all = res?.requisitions || [];
      setProjectOptions([...new Set(all.map(r => r.project_name).filter(Boolean))].sort());
      setDepartmentOptions([...new Set(all.map(r => r.department_name).filter(Boolean))].sort());
      setRequestTypeOptions([...new Set(all.map(r => r.request_type_name).filter(Boolean))].sort());
    }).catch(() => {});
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.listApprovedRequisitionsForVoucher({
        skip: page.skip,
        limit: page.limit,
        search: search || undefined,
        project_name: projectFilter || undefined,
        department_name: departmentFilter || undefined,
        request_type_name: requestTypeFilter || undefined,
      });
      setRows(res?.requisitions || []);
      setTotalCount(res?.total_count ?? 0);
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, search, projectFilter, departmentFilter, requestTypeFilter, toast]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when any filter changes
  const prevFilters = useRef({ search, projectFilter, departmentFilter, requestTypeFilter });
  useEffect(() => {
    const prev = prevFilters.current;
    if (
      prev.search !== search ||
      prev.projectFilter !== projectFilter ||
      prev.departmentFilter !== departmentFilter ||
      prev.requestTypeFilter !== requestTypeFilter
    ) {
      prevFilters.current = { search, projectFilter, departmentFilter, requestTypeFilter };
      setPage(p => ({ ...p, skip: 0 }));
    }
  }, [search, projectFilter, departmentFilter, requestTypeFilter]);

  // Status filter is client-side only — it derives from server-returned labels
  const statuses = REQ_PAYMENT_STATUSES.filter(s => rows.some(r => r.status_label === s));
  const filtered = statusFilter ? rows.filter(r => r.status_label === statusFilter) : rows;

  const displayed = sortKey
    ? [...filtered].sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = NUMERIC_COLS.has(sortKey)
          ? parseFloat(av || 0) - parseFloat(bv || 0)
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const sh = (label, key) => (
    <SortHeader label={label} colKey={key} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
  );

  const handleLoad = async (row) => {
    setLoadingRowId(row.purchase_request_id);
    try {
      const detail = await api.getRequisitionForVoucherLoad(row.purchase_request_id);
      onLoad(detail);
    } catch (e) {
      toast.show(e.message, 'error');
      load({ silent: true });
    } finally {
      setLoadingRowId(null);
    }
  };

  const handleMarkDone = async () => {
    if (!markingRow) return;
    setMarkBusy(true);
    try {
      await api.markRequisitionFullyProcessed(markingRow.purchase_request_id);
      toast.show(`${markingRow.pr_number} marked as fully processed.`, 'success');
      setMarkingRow(null);
      load({ silent: true });
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setMarkBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <BackButton onClick={onBack} style={{ marginBottom: 12 }}>← Back to Payment Voucher Engine</BackButton>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>
          Load Approved Requisition — Partial Quantity &amp; Multi-Vendor Engine
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
          Select an approved requisition and load only the outstanding quantities and amounts into a payment voucher. Previously processed quantities remain locked.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Input icon="🔍" placeholder="Search ref, requester, project or status..." value={search} onChange={setSearch} />
        </div>
        <div style={{ width: 190 }}>
          <Select value={projectFilter} onChange={setProjectFilter} options={[{ value: '', label: 'All Projects / Sites' }, ...projectOptions.map(p => ({ value: p, label: p }))]} />
        </div>
        <div style={{ width: 210 }}>
          <Select value={departmentFilter} onChange={setDepartmentFilter} options={[{ value: '', label: 'All Departments' }, ...departmentOptions.map(d => ({ value: d, label: d }))]} />
        </div>
        <div style={{ width: 190 }}>
          <Select value={requestTypeFilter} onChange={setRequestTypeFilter} options={[{ value: '', label: 'All Request Types' }, ...requestTypeOptions.map(t => ({ value: t, label: t }))]} />
        </div>
        <div style={{ width: 200 }}>
          <Select value={statusFilter} onChange={setStatusFilter} options={[{ value: '', label: 'All Statuses' }, ...statuses.map(s => ({ value: s, label: s }))]} />
        </div>
      </div>

      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {totalCount} requisition{totalCount === 1 ? '' : 's'} available
      </div>

      {markingRow && (
        <Modal title="Mark as Fully Processed" onClose={() => !markBusy && setMarkingRow(null)} width={460}>
          <p style={{ margin: '0 0 18px', fontSize: 13.5, color: '#1e293b', lineHeight: 1.6 }}>
            Mark <strong>{markingRow.pr_number}</strong> as fully processed?<br />
            <span style={{ fontSize: 12.5, color: '#64748b' }}>
              This will remove it from the load queue. Use this when no further payment is needed against this requisition.
            </span>
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setMarkingRow(null)} disabled={markBusy}>Cancel</Btn>
            <Btn onClick={handleMarkDone} disabled={markBusy}
              style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff' }}>
              {markBusy ? <Spinner size={14} /> : '✓ Fully Processed'}
            </Btn>
          </div>
        </Modal>
      )}

      <Card style={{ border: '1px solid black', borderRadius: 8, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading requisitions…" /></div>
        ) : (
          <>
            <Table
              cols={[
                { key: 'pr_number', label: sh('REF', 'pr_number'), align: 'center', render: v => <span style={{ fontFamily: 'var(--mono)', color: '#0b318b', fontWeight: 700 }}>{v || '—'}</span> },
                { key: 'requested_by_name', label: sh('REQUESTER', 'requested_by_name'), align: 'center', render: v => v || '—' },
                { key: 'department_name', label: sh('DEPARTMENT', 'department_name'), align: 'center', render: v => v || '—' },
                { key: 'request_type_name', label: sh('TYPE', 'request_type_name'), align: 'center', render: v => v || '—' },
                { key: 'project_name', label: sh('PROJECT', 'project_name'), align: 'center', render: v => v || '—' },
                { key: 'approved_amount', label: sh('APPROVED', 'approved_amount'), align: 'center', render: v => <span style={{ fontWeight: 700 }}>{fmt(v)}</span> },
                { key: 'processed_amount', label: sh('PROCESSED', 'processed_amount'), align: 'center', render: v => <span style={{ color: '#2563eb' }}>{fmt(v)}</span> },
                { key: 'balance_amount', label: sh('BALANCE', 'balance_amount'), align: 'center', render: v => <span style={{ fontWeight: 700, color: '#16a34a' }}>{fmt(v)}</span> },
                { key: 'lines_available', label: sh('LINES', 'lines_available'), align: 'center', render: v => `${v} line${v === 1 ? '' : 's'}` },
                {
                  key: 'status_label', label: sh('STATUS', 'status_label'), align: 'center', render: (v, row) => {
                    const sc = REQ_PAYMENT_STATUS_COLORS[v] || { bg: '#f1f5f9', color: '#475569' };
                    return (
                      <span
                        title={row.payment_hold_reason || undefined}
                        style={{ background: sc.bg, color: sc.color, border: sc.border ? `1px solid ${sc.border}` : 'none', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}
                      >{v}</span>
                    );
                  }
                },
                {
                  key: 'purchase_request_id', label: 'ACTION', align: 'center', render: (_, row) => {
                    const blocked = row.is_loadable === false;
                    const canMarkDone = parseFloat(row.balance_amount || 0) > 0 && parseFloat(row.processed_amount || 0) > 0;
                    return (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                        <Btn
                          size="sm"
                          disabled={blocked}
                          loading={loadingRowId === row.purchase_request_id}
                          title={blocked ? `${row.status_label}${row.payment_hold_reason ? ` — ${row.payment_hold_reason}` : ''}` : undefined}
                          onClick={() => handleLoad(row)}
                          style={{ fontSize: 11, padding: '3px 8px' }}
                        >Load →</Btn>
                        {canMarkDone && (
                          <Btn
                            size="sm"
                            onClick={() => setMarkingRow(row)}
                            title="Mark this requisition as fully processed and remove from queue"
                            style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff', fontSize: 11, padding: '3px 8px' }}
                          >Fully Processed</Btn>
                        )}
                      </div>
                    );
                  }
                },
              ]}
              rows={displayed}
              emptyMsg="No approved requisitions with an outstanding balance right now."
            />
            <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
